import React, { useEffect, useRef, useState } from "react";
import heroImage2 from "../../assets/main/Rectangle1.png";
import itemOmniVision from "../../assets/main/ItemOmnivision.png";
import itemGoodix from "../../assets/main/ItemGoodix.png";
import itemSyncomm from "../../assets/main/ItemSyncomm.png";
import itemQuectel from "../../assets/main/ItemQuectel.png";
import itemIkotek from "../../assets/main/ItemIkotek.png";
// import technologyAutomotive from "../../assets/technologies/automotiveImage01.png";
// import technologyTelematics from "../../assets/technologies/telematicsImage02.png";
// import technologyMobile from "../../assets/technologies/mobileImage01.png";
// import technologySecurity from "../../assets/technologies/securityImage01.png";
// import technologyMedical from "../../assets/technologies/medicalImage01.png";
// import technologyBlackbox from "../../assets/technologies/blackboxImage01.png";
// import technologyDems from "../../assets/technologies/demsImage01.png";
import eplusLogo from "../../assets/eplusLogo.png";
import { useLanguage } from "../../context/LanguageContext";
import { landingCopy } from "../../i18n/landingCopy";

const fallbackImage =
  "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=1600";

const layerSources = [heroImage2];
const layerFilters = ["grayscale(0.24) contrast(1.05) brightness(0.78)"];
const vendorMarqueeItems = [
  { name: "OmniVision", src: itemOmniVision },
  { name: "Goodix", src: itemGoodix },
  { name: "Syncomm", src: itemSyncomm },
  { name: "Quectel", src: itemQuectel },
  { name: "Ikotek", src: itemIkotek },
];
/*
const technologyMarqueeItems = [
  { name: "Automotive", src: technologyAutomotive },
  { name: "Telematics", src: technologyTelematics },
  { name: "Mobile", src: technologyMobile },
  { name: "Security", src: technologySecurity },
  { name: "Medical", src: technologyMedical },
  { name: "Blackbox", src: technologyBlackbox },
  { name: "Dems", src: technologyDems },
];
*/

const revealStyle = (delay: number) =>
  ({ transitionDelay: `${delay}ms` }) as React.CSSProperties;

const HalideLanding: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const copy = landingCopy[language] || landingCopy.en;
  const { hero, sections, cards } = copy;
  const {
    metrics,
    directionCards,
    partnerSignals,
    applicationCards,
    workflowSteps,
    highlights,
    contactCards,
    actionButtons,
    operatingSignals,
  } = cards;
  const shellRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const sceneSyncRef = useRef<(() => void) | null>(null);
  const scrollProgressRef = useRef(0);
  const expandedRef = useRef(false);
  const [heroImageUrls, setHeroImageUrls] = useState<string[]>([
    ...layerSources,
  ]);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isCardExpanded, setIsCardExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    const preload = (src: string) =>
      new Promise<string>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = () => resolve(fallbackImage);
        img.src = src;
      });

    Promise.all(layerSources.map((src) => preload(src))).then((values) => {
      if (alive) setHeroImageUrls(values);
    });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    expandedRef.current = isCardExpanded;
    sceneSyncRef.current?.();
  }, [isCardExpanded]);

  useEffect(() => {
    const handleScroll = () => {
      const maxScrollWindow = Math.max(window.innerHeight * 0.95, 1);
      const nextProgress = Math.min(
        Math.max(window.scrollY / maxScrollWindow, 0),
        1,
      );

      scrollProgressRef.current = nextProgress;
      setScrollProgress(nextProgress);
      sceneSyncRef.current?.();
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const root = shellRef.current;
    if (!root) return;

    const revealNodes = Array.from(
      root.querySelectorAll<HTMLElement>("[data-reveal]"),
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.16,
        rootMargin: "0px 0px -10% 0px",
      },
    );

    revealNodes.forEach((node) => observer.observe(node));

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const layer = canvas.querySelector(".layer") as HTMLDivElement | null;
    let isReady = false;
    let isHovered = false;
    let offsetX = 0;
    let offsetY = 0;
    let settleTimeout: number | undefined;

    const applyScene = () => {
      const scrollDepth = scrollProgressRef.current;
      const canvasScale = isReady ? 1.02 - scrollDepth * 0.12 : 0.94;
      const canvasLift = -scrollDepth * 94;
      const layerScaleBase = isReady
        ? expandedRef.current
          ? 1.08
          : isHovered
            ? 1.05
            : 1.02
        : 0.98;
      const layerScale = Math.max(layerScaleBase - scrollDepth * 0.06, 0.9);
      const detailLift = expandedRef.current ? -18 : 0;

      canvas.style.opacity = isReady ? "1" : "0";
      canvas.style.transform = `translate(${offsetX}px, ${
        offsetY + canvasLift
      }px) scale(${Math.max(canvasScale, 0.84)})`;

      if (!layer) return;

      layer.style.filter = isReady
        ? isHovered
          ? "grayscale(0.12) contrast(1.14) brightness(0.92) blur(0px)"
          : expandedRef.current
            ? "grayscale(0.16) contrast(1.12) brightness(0.87) blur(0px)"
            : `${layerFilters[0] ?? "none"} blur(0px)`
        : `${layerFilters[0] ?? "none"} blur(8px)`;
      layer.style.transform = `translate(${offsetX * 0.24}px, ${
        offsetY * 0.2 + canvasLift * 0.18 + detailLift
      }px) scale(${layerScale})`;
      layer.style.opacity = isReady ? String(1 - scrollDepth * 0.08) : "0.84";
      layer.style.boxShadow = isReady
        ? isHovered
          ? "0 0 28px rgba(100, 63, 218, 0.28), 0 0 54px rgba(196, 161, 255, 0.22)"
          : expandedRef.current
            ? "0 0 34px rgba(100, 63, 218, 0.3), 0 0 66px rgba(196, 161, 255, 0.24)"
            : "0 0 22px rgba(100, 63, 218, 0.22), 0 0 44px rgba(196, 161, 255, 0.18)"
        : "none";
    };

    sceneSyncRef.current = applyScene;

    canvas.style.transition =
      "opacity 1.4s cubic-bezier(0.16, 1, 0.3, 1), transform 1.6s cubic-bezier(0.16, 1, 0.3, 1)";
    if (layer) {
      layer.style.transition =
        "filter 1.4s cubic-bezier(0.16, 1, 0.3, 1), transform 1.6s cubic-bezier(0.16, 1, 0.3, 1), opacity 1.2s ease, box-shadow 0.4s ease";
    }
    applyScene();

    const timeout = window.setTimeout(() => {
      isReady = true;
      applyScene();
      settleTimeout = window.setTimeout(() => {
        canvas.style.transition = "transform 0.28s ease-out, opacity 0.6s ease";
        if (layer) {
          layer.style.transition =
            "filter 0.28s ease-out, transform 0.28s ease-out, opacity 0.28s ease, box-shadow 0.28s ease";
        }
      }, 900);
    }, 200);

    const handleLayerEnter = () => {
      if (!layer || !isReady) return;
      isHovered = true;
      applyScene();
    };

    const handleLayerLeave = () => {
      if (!layer || !isReady) return;
      isHovered = false;
      applyScene();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isReady) return;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      offsetX = (e.clientX - centerX) * 0.012;
      offsetY = (e.clientY - centerY) * 0.01;
      applyScene();
    };

    const handleMouseLeaveWindow = () => {
      if (!isReady) return;
      offsetX = 0;
      offsetY = 0;
      applyScene();
    };

    if (layer) {
      layer.addEventListener("mouseenter", handleLayerEnter);
      layer.addEventListener("mouseleave", handleLayerLeave);
    }
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeaveWindow);

    return () => {
      clearTimeout(timeout);
      if (settleTimeout) clearTimeout(settleTimeout);
      sceneSyncRef.current = null;
      if (layer) {
        layer.removeEventListener("mouseenter", handleLayerEnter);
        layer.removeEventListener("mouseleave", handleLayerLeave);
      }
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeaveWindow);
    };
  }, []);

  const textFade = Math.max(0.18, 1 - scrollProgress * 0.88);
  const textShift = -scrollProgress * 34;
  const panelVisibility = isCardExpanded ? 1 : 0;
  const panelLift = isCardExpanded ? 0 : 18;

  return (
    <>
      <style>{`
        :root {
          --bg: #06061a;
          --bg-soft: #0d0d2b;
          --silver: #f2f4ff;
          --accent: #C4A1FF;
          --signal: #643FDA;
          --surface: #13123a;
          --surface-2: #0b0b28;
          --muted: rgba(242, 244, 255, 0.72);
          --grain-opacity: 0.14;
        }

        html,
        body {
          overflow-x: hidden;
          overflow-y: auto;
          -ms-overflow-style: none;
          scrollbar-width: none;
          background: var(--bg);
        }

        html::-webkit-scrollbar,
        body::-webkit-scrollbar {
          display: none;
        }

        a {
          color: inherit;
        }

        .experience-shell {
          position: relative;
          min-height: 100vh;
          background:
            linear-gradient(180deg, rgba(6, 6, 26, 1) 0%, rgba(20, 14, 50, 0.95) 30%, rgba(40, 28, 80, 0.4) 60%, rgba(255, 255, 255, 0.06) 100%);
        }

        .hero-stage {
          position: relative;
          height: 102vh;
        }

        .hero-sticky {
          position: sticky;
          top: 0;
          height: 100vh;
          overflow: hidden;
        }

        .halide-body {
          background:
            radial-gradient(circle at 20% 18%, rgba(100, 63, 218, 0.22), transparent 42%),
            radial-gradient(circle at 82% 82%, rgba(255, 255, 255, 0.08), transparent 34%),
            radial-gradient(circle at 50% 90%, rgba(255, 255, 255, 0.05), transparent 50%),
            linear-gradient(180deg, rgba(6, 6, 26, 0.84), rgba(16, 12, 40, 0.9)),
            var(--bg);
          color: var(--silver);
          font-family: "Syncopate", sans-serif;
          overflow: hidden;
          height: 100vh;
          width: 100%;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .hero-glow {
          position: absolute;
          inset: auto auto 9rem 50%;
          width: min(72vw, 48rem);
          height: 14rem;
          transform: translateX(-50%);
          background: radial-gradient(circle, rgba(100, 63, 218, 0.28), rgba(100, 63, 218, 0.05) 48%, transparent 72%);
          filter: blur(26px);
          pointer-events: none;
          opacity: 0.82;
        }

        .top-logo {
          position: absolute;
          top: 1.25rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 20;
          pointer-events: none;
        }

        .top-logo img {
          width: 116px;
          height: auto;
          filter: drop-shadow(0 8px 18px rgba(4, 7, 16, 0.55));
        }

        .halide-grain {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 100;
          opacity: var(--grain-opacity);
        }

        .viewport {
          perspective: 2000px;
          width: 100%;
          height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          isolation: isolate;
        }

        .canvas-3d {
          position: relative;
          width: min(920px, calc(100vw - 4rem));
          aspect-ratio: 3 / 2;
          transform-style: preserve-3d;
          pointer-events: auto;
        }

        .layer {
          position: absolute;
          inset: 0;
          display: block;
          background-size: cover;
          background-position: center;
          border: 1px solid rgba(224, 224, 224, 0.1);
          background-repeat: no-repeat;
          opacity: 1;
          transition:
            filter 1.4s cubic-bezier(0.16, 1, 0.3, 1),
            transform 1.6s cubic-bezier(0.16, 1, 0.3, 1),
            opacity 1.2s ease,
            box-shadow 0.4s ease;
          will-change: transform;
          cursor: pointer;
        }

        .interface-grid {
          position: absolute;
          inset: 0;
          padding: 4rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: auto 1fr auto;
          z-index: 10;
          pointer-events: none;
          transition: opacity 0.3s ease, transform 0.3s ease;
        }

        .hero-title {
          grid-column: 1 / -1;
          align-self: center;
          font-size: clamp(3.2rem, 10vw, 10rem);
          line-height: 0.84;
          letter-spacing: -0.05em;
          text-transform: uppercase;
          text-shadow: 0 0 28px rgba(100, 63, 218, 0.45);
          color: var(--silver);
          margin: 0;
        }

        .hero-title-subline {
          display: block;
          font-size: 0.5em;
        }

        .hero-subtitle {
          grid-column: 1 / -1;
          margin-top: 2rem;
          font-family: system-ui, sans-serif;
          letter-spacing: 0.12em;
          font-size: 0.92rem;
          line-height: 1.85;
          color: rgba(245, 247, 255, 0.86);
          max-width: 58rem;
        }

        .brand-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.72rem;
          letter-spacing: 0.16em;
          color: var(--signal);
          text-transform: uppercase;
        }

        .brand-chip::before {
          content: "";
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: var(--signal);
          box-shadow: 0 0 0 4px rgba(100, 63, 218, 0.28);
        }

        .cta-row {
          display: flex;
          gap: 0.9rem;
          align-items: center;
          margin-top: 2rem;
        }

        .cta-button {
          pointer-events: auto;
          background: linear-gradient(135deg, rgba(100, 63, 218, 0.22), rgba(196, 161, 255, 0.18));
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: var(--silver);
          padding: 1rem 1.7rem;
          text-decoration: none;
          font-weight: 700;
          clip-path: polygon(0 0, 100% 0, 100% 70%, 85% 100%, 0 100%);
          transition: 0.3s;
          font-size: 0.82rem;
          letter-spacing: 0.08em;
        }

        .cta-button:hover {
          background: linear-gradient(135deg, rgba(100, 63, 218, 0.35), rgba(196, 161, 255, 0.28));
          color: var(--silver);
          transform: translateY(-5px);
        }

        .cta-button.secondary {
          background: rgba(255, 255, 255, 0.08);
          color: var(--silver);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }

        .hero-note {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.72rem;
          letter-spacing: 0.14em;
          color: rgba(242, 244, 255, 0.62);
          text-transform: uppercase;
        }

        .card-hint {
          position: absolute;
          left: 50%;
          bottom: 7.4rem;
          transform: translateX(-50%);
          z-index: 12;
          padding: 0.45rem 0.85rem;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(10, 14, 28, 0.48);
          backdrop-filter: blur(16px);
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.67rem;
          letter-spacing: 0.16em;
          color: rgba(242, 244, 255, 0.76);
          text-transform: uppercase;
          pointer-events: none;
          transition: opacity 0.25s ease, transform 0.25s ease;
        }

        .hero-language-switch {
          z-index: 30;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.5rem;
          margin-top: 1.5rem;
          align-self: flex-end;
          position: relative;
          z-index: 50;
          pointer-events: auto;
        }

        .hero-network-box {
          display: inline-flex;
          flex-direction: column;
          align-items: flex-end;
          text-align: right;
          margin-left: auto;
          pointer-events: auto;
        }

        .hero-language-button {
          border: 1px solid rgba(255, 255, 255, 0.42);
          background: transparent;
          color: var(--silver);
          pointer-events: auto;
          width: 64px;
          height: 34px;
          border-radius: 999px;
          padding: 0;
          font-size: 0.7rem;
          cursor: pointer;
          font-weight: 700;
          letter-spacing: 0.12em;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.32rem;
          line-height: 1;
          transition: background 0.2s ease, color 0.2s ease;
        }

        .hero-language-button:hover {
          background: rgba(255, 255, 255, 0.18);
        }

        .hero-language-button.is-active {
          background: rgba(255, 255, 255, 0.22);
          color: #fff;
        }

        .hero-since-line {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          color: var(--accent);
          font-size: 0.7rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          margin-top: 0.35rem;
        }

        .focus-panel {
          position: absolute;
          right: 4rem;
          bottom: 5rem;
          width: min(380px, calc(100vw - 3rem));
          padding: 1.1rem 1.2rem 1.25rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background: linear-gradient(180deg, rgba(12, 18, 34, 0.82), rgba(7, 10, 20, 0.9));
          backdrop-filter: blur(18px);
          box-shadow: 0 22px 55px rgba(0, 0, 0, 0.28);
          z-index: 12;
          transition: opacity 0.35s ease, transform 0.35s ease;
        }

        .focus-kicker {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.68rem;
          letter-spacing: 0.18em;
          color: var(--signal);
          text-transform: uppercase;
        }

        .focus-title {
          margin: 0.7rem 0 0.55rem;
          font-size: 1.08rem;
          letter-spacing: -0.02em;
          line-height: 1.35;
        }

        .focus-copy {
          margin: 0;
          font-family: system-ui, sans-serif;
          font-size: 0.92rem;
          line-height: 1.68;
          color: rgba(242, 244, 255, 0.76);
        }

        .focus-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 1rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.72rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .focus-action {
          color: var(--silver);
          opacity: 0.8;
        }

        .scroll-hint {
          position: absolute;
          bottom: 2rem;
          left: 50%;
          width: 1px;
          height: 60px;
          background: linear-gradient(to bottom, var(--silver), transparent);
          animation: flow 2s infinite ease-in-out;
        }

        @keyframes flow {
          0%, 100% { transform: scaleY(0); transform-origin: top; }
          50% { transform: scaleY(1); transform-origin: top; }
          51% { transform: scaleY(1); transform-origin: bottom; }
        }

        .section-shell {
          position: relative;
          padding: 7.5rem 3rem;
          background:
            radial-gradient(circle at 50% 0%, rgba(100, 63, 218, 0.1), transparent 32%),
            radial-gradient(circle at 50% 100%, rgba(255, 255, 255, 0.04), transparent 40%),
            linear-gradient(180deg, rgba(6, 6, 22, 0.9), rgba(14, 10, 36, 0.95), rgba(20, 16, 44, 1));
          overflow: hidden;
        }

        .section-shell.section-shell-tight {
          padding-top: 3.5rem;
        }

        .section-shell::before {
          content: "";
          position: absolute;
          inset: 0;
          background:
            linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.03) 50%, transparent 100%);
          opacity: 0.4;
          pointer-events: none;
        }

        .section-head {
          position: relative;
          z-index: 1;
          max-width: 780px;
          margin: 0 auto 2.6rem;
          text-align: center;
        }

        .section-kicker {
          margin: 0 0 0.9rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.72rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--signal);
        }

        .section-title {
          margin: 0;
          font-size: clamp(2.2rem, 5vw, 4.4rem);
          line-height: 0.96;
          letter-spacing: -0.05em;
          color: var(--silver);
        }

        .section-copy {
          margin: 1.15rem auto 0;
          max-width: 700px;
          font-family: system-ui, sans-serif;
          font-size: 1rem;
          line-height: 1.75;
          color: var(--muted);
        }

        .reveal-block {
          opacity: 0;
          transform: translateY(42px) scale(0.985);
          filter: blur(12px);
          transition:
            opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1),
            transform 0.85s cubic-bezier(0.16, 1, 0.3, 1),
            filter 0.85s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .reveal-block.is-visible {
          opacity: 1;
          transform: translateY(0) scale(1);
          filter: blur(0);
        }

        .metric-row {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto 3rem;
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 1rem;
        }

        .metric-card {
          position: relative;
          overflow: hidden;
          padding: 1.3rem 1.2rem 1.4rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: linear-gradient(180deg, rgba(13, 18, 34, 0.84), rgba(9, 12, 23, 0.96));
          box-shadow: 0 18px 38px rgba(0, 0, 0, 0.2);
        }

        .metric-card::after {
          content: "";
          position: absolute;
          top: 0;
          left: -20%;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
          transform: skewX(-18deg);
          animation: metricSweep 5.2s linear infinite;
          opacity: 0.7;
        }

        @keyframes metricSweep {
          0% { transform: translateX(-180%) skewX(-18deg); }
          100% { transform: translateX(420%) skewX(-18deg); }
        }

        .metric-label {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.7rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(242, 244, 255, 0.62);
        }

        .metric-value {
          margin-top: 0.85rem;
          font-size: clamp(1.7rem, 4vw, 3rem);
          line-height: 0.95;
          letter-spacing: -0.04em;
          color: #BDA7C8;
        }

        .metric-detail {
          margin-top: 0.75rem;
          font-family: system-ui, sans-serif;
          font-size: 0.92rem;
          line-height: 1.65;
          color: var(--muted);
        }

        .direction-grid,
        .application-grid,
        .contact-grid {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          gap: 1rem;
        }

        .direction-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .application-grid {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .contact-grid {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        .info-card,
        .application-card,
        .contact-card {
          position: relative;
          overflow: hidden;
          padding: 1.4rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: linear-gradient(180deg, rgba(14, 19, 35, 0.82), rgba(9, 12, 23, 0.96));
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.22);
        }

        .info-card::before,
        .application-card::before,
        .contact-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, rgba(100, 63, 218, 0.85), rgba(196, 161, 255, 0.7));
          opacity: 0.85;
        }

        .card-code {
          display: inline-block;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.7rem;
          letter-spacing: 0.18em;
          color: var(--accent);
          text-transform: uppercase;
        }

        .card-title {
          margin: 0.8rem 0 0.55rem;
          font-size: 1.04rem;
          line-height: 1.35;
          color: #BDA7C8;
        }

        .card-body {
          margin: 0;
          font-family: system-ui, sans-serif;
          font-size: 0.95rem;
          line-height: 1.7;
          color: var(--muted);
        }

        .partner-layout,
        .workflow-layout,
        .final-layout {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 1rem;
        }

        .partner-panel,
        .workflow-panel,
        .contact-panel,
        .signal-panel {
          position: relative;
          overflow: hidden;
          padding: 1.6rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: linear-gradient(180deg, rgba(14, 19, 35, 0.82), rgba(9, 12, 23, 0.96));
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.22);
        }

        .partner-copy,
        .workflow-copy,
        .contact-copy {
          font-family: system-ui, sans-serif;
          font-size: 0.98rem;
          line-height: 1.78;
          color: var(--muted);
        }

        .partner-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 1.4rem;
        }

        .partner-chip {
          padding: 0.72rem 0.9rem;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(255, 255, 255, 0.04);
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.72rem;
          letter-spacing: 0.12em;
          color: rgba(242, 244, 255, 0.78);
          text-transform: uppercase;
          animation: chipFloat 6s ease-in-out infinite;
          animation-delay: calc(var(--chip-index, 0) * 140ms);
        }

        @keyframes chipFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }

        .signal-stack {
          display: grid;
          gap: 0.9rem;
        }

        .signal-bar {
          padding: 1rem 1.05rem;
          border-left: 1px solid rgba(100, 63, 218, 0.5);
          background: linear-gradient(90deg, rgba(100, 63, 218, 0.1), rgba(255, 255, 255, 0.02));
        }

        .signal-bar strong {
          display: block;
          font-size: 0.92rem;
          margin-bottom: 0.35rem;
          color: #BDA7C8;
        }

        .signal-bar span {
          font-family: system-ui, sans-serif;
          font-size: 0.92rem;
          line-height: 1.6;
          color: var(--muted);
        }

        .workflow-rail {
          display: grid;
          gap: 1rem;
          margin-top: 1.25rem;
        }

        .workflow-step {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 1rem;
          align-items: start;
          padding: 1rem 0;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
        }

        .workflow-step:first-child {
          border-top: 0;
        }

        .workflow-index {
          width: 3rem;
          height: 3rem;
          border-radius: 999px;
          border: 1px solid rgba(100, 63, 218, 0.3);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.78rem;
          letter-spacing: 0.16em;
          color: var(--signal);
        }

        .workflow-step h3 {
          margin: 0.15rem 0 0.45rem;
          font-size: 1rem;
          color: #BDA7C8;
        }

        .workflow-step p {
          margin: 0;
          font-family: system-ui, sans-serif;
          font-size: 0.95rem;
          line-height: 1.7;
          color: var(--muted);
        }

        .highlight-band {
          display: grid;
          gap: 0.9rem;
        }

        .highlight-tile {
          padding: 1rem 1.05rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(255, 255, 255, 0.035);
        }

        .highlight-tile strong {
          display: block;
          margin-bottom: 0.35rem;
          font-size: 0.9rem;
          color: #BDA7C8;
        }

        .highlight-tile span {
          font-family: system-ui, sans-serif;
          font-size: 0.92rem;
          line-height: 1.6;
          color: var(--muted);
        }

        .contact-card-label {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.7rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(242, 244, 255, 0.62);
        }

        .contact-card-value {
          margin-top: 0.85rem;
          font-family: system-ui, sans-serif;
          font-size: 0.98rem;
          line-height: 1.7;
          color: var(--silver);
        }

        .contact-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.85rem;
          margin-top: 1.4rem;
        }

        .contact-action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 12rem;
          padding: 0.95rem 1.2rem;
          text-decoration: none;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(255, 255, 255, 0.04);
          color: var(--silver);
          font-size: 0.78rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          transition: transform 0.25s ease, border-color 0.25s ease, background 0.25s ease;
        }

        .contact-action:hover {
          transform: translateY(-4px);
          border-color: rgba(100, 63, 218, 0.3);
          background: rgba(100, 63, 218, 0.1);
        }

        .section-nav-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          margin-top: 2.2rem;
          padding: 0.85rem 1.6rem;
          text-decoration: none;
          border: 1px solid rgba(100, 63, 218, 0.35);
          background: linear-gradient(135deg, rgba(100, 63, 218, 0.12), rgba(196, 161, 255, 0.08));
          color: var(--silver);
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.75rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          transition: transform 0.25s ease, border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
          position: relative;
          z-index: 1;
        }

        .section-nav-btn:hover {
          transform: translateY(-4px);
          border-color: rgba(100, 63, 218, 0.55);
          background: linear-gradient(135deg, rgba(100, 63, 218, 0.22), rgba(196, 161, 255, 0.16));
          box-shadow: 0 8px 24px rgba(100, 63, 218, 0.2);
        }

        .section-nav-btn::after {
          content: "→";
          font-size: 0.85rem;
          transition: transform 0.25s ease;
        }

        .section-nav-btn:hover::after {
          transform: translateX(4px);
        }

        .vendor-marquee-shell {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 2.8rem auto 0;
        }

        .vendor-marquee-window {
          --marquee-gap: 1rem;
          position: relative;
          overflow: hidden;
          padding: 0.45rem 0;
          mask-image: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 1) 9%, rgba(0, 0, 0, 1) 91%, transparent 100%);
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 1) 9%, rgba(0, 0, 0, 1) 91%, transparent 100%);
        }

        .vendor-marquee-track {
          display: flex;
          align-items: center;
          gap: var(--marquee-gap);
          width: max-content;
          animation: vendorMarquee 24s linear infinite;
        }

        .vendor-marquee-window:hover .vendor-marquee-track {
          animation-play-state: paused;
        }

        .vendor-marquee-item {
          flex: 0 0 auto;
          width: 220px;
          height: 88px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.18rem 0.32rem;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.16);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(244, 246, 255, 0.92));
          box-shadow: 0 16px 34px rgba(0, 0, 0, 0.22);
        }

        .vendor-marquee-item img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transform: scale(1.08);
          filter: saturate(1.02) contrast(1.02);
        }

        .technology-marquee-shell {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 2.8rem auto 0;
        }

        .technology-marquee-window {
          --marquee-gap: 1rem;
          position: relative;
          overflow: hidden;
          padding: 0.45rem 0;
          mask-image: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 1) 9%, rgba(0, 0, 0, 1) 91%, transparent 100%);
          -webkit-mask-image: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 1) 9%, rgba(0, 0, 0, 1) 91%, transparent 100%);
        }

        .technology-marquee-track {
          display: flex;
          align-items: center;
          gap: var(--marquee-gap);
          width: max-content;
          animation: vendorMarquee 22s linear infinite;
        }

        .technology-marquee-window:hover .technology-marquee-track {
          animation-play-state: paused;
        }

        .technology-marquee-item {
          position: relative;
          flex: 0 0 auto;
          width: 220px;
          height: 88px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.12);
          background: rgba(255, 255, 255, 0.06);
          box-shadow: 0 16px 34px rgba(0, 0, 0, 0.22);
        }

        .technology-marquee-item::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(7, 10, 20, 0.08), rgba(7, 10, 20, 0.2));
          pointer-events: none;
        }

        .technology-marquee-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transform: scale(1.03);
        }

        @keyframes vendorMarquee {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(calc(-50% - (var(--marquee-gap) / 2)));
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .vendor-marquee-track {
            animation: none;
          }
        }

        .footer-band {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
          padding-top: 3.5rem;
          padding-bottom: 1.5rem;
          border-top: 1px solid rgba(100, 63, 218, 0.2);
        }

        .footer-top {
          display: grid;
          grid-template-columns: 1.2fr 1fr 1fr 1fr;
          gap: 2rem;
          margin-bottom: 3rem;
        }

        .footer-brand {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .footer-brand img {
          width: 100px;
          height: auto;
          filter: drop-shadow(0 4px 12px rgba(100, 63, 218, 0.3));
        }

        .footer-brand-desc {
          font-family: system-ui, sans-serif;
          font-size: 0.88rem;
          line-height: 1.65;
          color: rgba(242, 244, 255, 0.55);
          max-width: 280px;
        }

        .footer-col-title {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.68rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--signal);
          margin-bottom: 1.2rem;
        }

        .footer-links {
          display: flex;
          flex-direction: column;
          gap: 0.7rem;
        }

        .footer-link {
          font-family: system-ui, sans-serif;
          font-size: 0.88rem;
          color: rgba(242, 244, 255, 0.6);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .footer-link:hover {
          color: var(--accent);
        }

        .footer-info-item {
          font-family: system-ui, sans-serif;
          font-size: 0.85rem;
          line-height: 1.7;
          color: rgba(242, 244, 255, 0.5);
        }

        .footer-info-label {
          color: rgba(242, 244, 255, 0.7);
          font-weight: 600;
          margin-right: 0.4rem;
        }

        .footer-divider {
          border: none;
          border-top: 1px solid rgba(255, 255, 255, 0.06);
          margin: 0 0 1.2rem;
        }

        .footer-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.7rem;
          letter-spacing: 0.14em;
          color: rgba(242, 244, 255, 0.4);
          text-transform: uppercase;
        }

        .footer-social {
          display: flex;
          gap: 0.6rem;
        }

        .footer-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.6rem;
          height: 2.6rem;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(255, 255, 255, 0.04);
          color: rgba(242, 244, 255, 0.6);
          text-decoration: none;
          font-size: 1.1rem;
          transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
        }

        .footer-social-link:hover {
          border-color: rgba(100, 63, 218, 0.4);
          background: rgba(100, 63, 218, 0.12);
          transform: translateY(-2px);
        }

        @media (max-width: 960px) {
          .footer-top {
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
          }
        }

        @media (max-width: 600px) {
          .footer-top {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 1100px) {
          .metric-row,
          .application-grid,
          .contact-grid,
          .direction-grid,
          .partner-layout,
          .workflow-layout,
          .final-layout {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 960px) {
          .hero-stage {
            height: 100vh;
          }

          .interface-grid {
            padding: 5rem 1.8rem 2rem 1.4rem;
            grid-template-columns: 1fr;
            row-gap: 1rem;
          }

          .hero-network-box {
            width: 100%;
            margin-left: 0;
            padding-right: 0.35rem;
            align-items: flex-end;
            text-align: right;
          }

          .hero-language-switch {
            width: 100%;
            justify-content: flex-end;
            padding-right: 0.15rem;
          }

          .vendor-marquee-shell {
            margin-top: 2.1rem;
          }

          .vendor-marquee-track {
            animation-duration: 18s;
          }

          .vendor-marquee-window {
            --marquee-gap: 0.8rem;
          }

          .vendor-marquee-item {
            width: 170px;
            height: 72px;
            padding: 0.12rem 0.24rem;
          }

          .technology-marquee-shell {
            margin-top: 2.1rem;
          }

          .technology-marquee-track {
            animation-duration: 16s;
          }

          .technology-marquee-window {
            --marquee-gap: 0.8rem;
          }

          .technology-marquee-item {
            width: 170px;
            height: 72px;
          }

          .canvas-3d {
            width: min(92vw, 760px);
          }

          .focus-panel {
            right: 1.25rem;
            left: 1.25rem;
            bottom: 4.4rem;
            width: auto;
          }

          .card-hint {
            bottom: 11rem;
          }

          .card-hint {
            display: none;
          }

          .section-shell {
            padding: 6rem 1.4rem;
          }

          .section-shell.section-shell-tight {
            padding-top: 2.4rem;
          }
        }
      `}</style>

      <div className="experience-shell" ref={shellRef}>
        <section className="hero-stage">
          <div className="hero-sticky">
            <div className="halide-body">
              <svg style={{ position: "absolute", width: 0, height: 0 }}>
                <filter id="grain">
                  <feTurbulence
                    type="fractalNoise"
                    baseFrequency="0.65"
                    numOctaves="3"
                  />
                  <feColorMatrix type="saturate" values="0" />
                </filter>
              </svg>

              <div className="halide-grain" style={{ filter: "url(#grain)" }} />
              <div className="hero-glow" />
              <div className="top-logo" aria-label="eplus logo">
                <img src={eplusLogo} alt="Eplus logo" />
              </div>

              <div
                className="interface-grid"
                style={{
                  opacity: textFade,
                  transform: `translateY(${textShift}px)`,
                }}
              >
                <div className="brand-chip">{hero.brandChip}</div>
                <div className="hero-network-box">
                  <div
                    style={{
                      fontFamily: "monospace",
                      color: "var(--accent)",
                      fontSize: "0.7rem",
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                    }}
                  >
                    <div>{hero.networkLine}</div>
                    <div className="hero-since-line">{hero.sinceLine}</div>
                  </div>

                  <div
                    className="hero-language-switch"
                    aria-label="language switch"
                  >
                    <button
                      type="button"
                      className={`hero-language-button${
                        language === "en" ? " is-active" : ""
                      }`}
                      onClick={() => setLanguage("en")}
                    >
                      <span role="img" aria-label="USA flag">
                        🇺🇸
                      </span>{" "}
                      EN
                    </button>
                    <button
                      type="button"
                      className={`hero-language-button${
                        language === "ko" ? " is-active" : ""
                      }`}
                      onClick={() => setLanguage("ko")}
                    >
                      <span role="img" aria-label="Korea flag">
                        🇰🇷
                      </span>{" "}
                      KO
                    </button>
                  </div>
                </div>

                <div style={{ alignSelf: "center" }}>
                  <h1 className="hero-title">
                    {hero.title}
                    <span className="hero-title-subline">
                      {hero.subtitleLine}
                    </span>
                  </h1>

                  <p className="hero-subtitle">{hero.subtitle}</p>

                  <div className="cta-row">
                    <a href="/technologies" className="cta-button">
                      {hero.ctaPrimary}
                    </a>
                    <a href="#overview" className="cta-button secondary">
                      {hero.ctaSecondary}
                    </a>
                  </div>
                </div>

                <div
                  style={{
                    gridColumn: "1 / -1",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    gap: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div className="hero-note">{hero.note1}</div>
                  <div className="hero-note">{hero.note2}</div>
                </div>
              </div>

              <div
                className="card-hint"
                style={{
                  opacity: Math.max(0, 0.9 - scrollProgress * 2.4),
                  transform: `translateX(-50%) translateY(${scrollProgress * 14}px)`,
                }}
              >
                {hero.cardHint}
              </div>

              <div className="viewport">
                <div className="canvas-3d" ref={canvasRef}>
                  {heroImageUrls.map((imageUrl, index) => (
                    <div
                      key={`hero-layer-${index}`}
                      className={`layer layer-${index + 1}`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isCardExpanded}
                      onClick={() => {
                        setIsCardExpanded((prev) => !prev);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setIsCardExpanded((prev) => !prev);
                        }
                      }}
                      style={{
                        backgroundImage: `url("${imageUrl}")`,
                      }}
                    />
                  ))}
                </div>
              </div>

              <div
                className="focus-panel"
                style={{
                  opacity: panelVisibility,
                  transform: `translateY(${panelLift}px)`,
                  pointerEvents: isCardExpanded ? "auto" : "none",
                }}
              >
                <div className="focus-kicker">{hero.focusKicker}</div>
                <h3 className="focus-title">{hero.focusTitle}</h3>
                <p className="focus-copy">{hero.focusCopy}</p>
                <div className="focus-actions">
                  {hero.focusActions.map((action) => (
                    <span className="focus-action" key={action}>
                      {action}
                    </span>
                  ))}
                </div>
              </div>

              <div
                className="scroll-hint"
                style={{ opacity: Math.max(0, 1 - scrollProgress * 2.6) }}
              />
            </div>
          </div>
        </section>

        <section className="section-shell section-shell-tight" id="overview">
          <div className="section-head reveal-block" data-reveal>
            <p className="section-kicker">{sections.overview.kicker}</p>
            <h2 className="section-title">{sections.overview.title}</h2>
            <p className="section-copy">{sections.overview.copy}</p>
          </div>

          <div className="metric-row">
            {metrics.map((item, index) => (
              <article
                key={item.id}
                className="metric-card reveal-block"
                data-reveal
                style={revealStyle(70 + index * 90)}
              >
                <div className="metric-label">{item.label}</div>
                <div className="metric-value">{item.value}</div>
                <p className="metric-detail">{item.detail}</p>
              </article>
            ))}
          </div>

          <div className="direction-grid">
            {directionCards.map((card, index) => (
              <article
                key={card.id}
                className="info-card reveal-block"
                data-reveal
                style={revealStyle(130 + index * 90)}
              >
                <span className="card-code">{card.eyebrow}</span>
                <h3 className="card-title">{card.title}</h3>
                <p className="card-body">{card.body}</p>
              </article>
            ))}
          </div>

          <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <a
              href="/company"
              className="section-nav-btn reveal-block"
              data-reveal
              style={revealStyle(400)}
            >
              {sections.overview.button}
            </a>
          </div>
        </section>

        <section className="section-shell">
          <div className="partner-layout">
            <article
              className="partner-panel reveal-block"
              data-reveal
              style={revealStyle(80)}
            >
              <p className="section-kicker" style={{ textAlign: "left" }}>
                {sections.partnerNetwork.kicker}
              </p>
              <h2
                className="section-title"
                style={{
                  fontSize: "clamp(2rem, 4vw, 3.4rem)",
                  textAlign: "left",
                }}
              >
                {sections.partnerNetwork.title}
              </h2>
              <p className="partner-copy">{sections.partnerNetwork.copy}</p>
              <div className="partner-chip-row">
                {partnerSignals.map((chip, index) => (
                  <span
                    key={chip}
                    className="partner-chip"
                    style={
                      {
                        ["--chip-index" as string]: index,
                      } as React.CSSProperties
                    }
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </article>

            <article
              className="signal-panel reveal-block"
              data-reveal
              style={revealStyle(170)}
            >
              <p className="section-kicker" style={{ textAlign: "left" }}>
                {sections.partnerNetwork.operatingSignals}
              </p>
              <div className="signal-stack">
                {operatingSignals.map((signal) => (
                  <div className="signal-bar" key={signal.id}>
                    <strong>{signal.title}</strong>
                    <span>{signal.body}</span>
                  </div>
                ))}
              </div>
            </article>
          </div>

          <div
            style={{
              textAlign: "center",
              position: "relative",
              zIndex: 1,
              marginTop: "1.5rem",
            }}
          >
            <a
              href="/vendor"
              className="section-nav-btn reveal-block"
              data-reveal
              style={revealStyle(260)}
            >
              {sections.partnerNetwork.button}
            </a>
          </div>

          <div
            className="vendor-marquee-shell reveal-block"
            data-reveal
            style={revealStyle(320)}
            aria-label="vendor logo marquee"
          >
            <div className="vendor-marquee-window">
              <div className="vendor-marquee-track">
                {[...vendorMarqueeItems, ...vendorMarqueeItems].map(
                  (item, index) => (
                    <div
                      key={`${item.name}-${index}`}
                      className="vendor-marquee-item"
                    >
                      <img src={item.src} alt={`${item.name} logo`} />
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell">
          <div className="section-head reveal-block" data-reveal>
            <p className="section-kicker">
              {sections.applicationCoverage.kicker}
            </p>
            <h2 className="section-title">
              {sections.applicationCoverage.title}
            </h2>
            <p className="section-copy">{sections.applicationCoverage.copy}</p>
          </div>

          <div className="application-grid">
            {applicationCards.map((card, index) => (
              <article
                key={card.code}
                className="application-card reveal-block"
                data-reveal
                style={revealStyle(70 + index * 80)}
              >
                <span className="card-code">{card.code}</span>
                <h3 className="card-title">{card.title}</h3>
                <p className="card-body">{card.body}</p>
              </article>
            ))}
          </div>

          <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <a
              href="/technologies"
              className="section-nav-btn reveal-block"
              data-reveal
              style={revealStyle(550)}
            >
              {sections.applicationCoverage.button}
            </a>
          </div>

          {/*
          <div
            className="technology-marquee-shell reveal-block"
            data-reveal
            style={revealStyle(610)}
            aria-label="technology image marquee"
          >
            <div className="technology-marquee-window">
              <div className="technology-marquee-track">
                {[...technologyMarqueeItems, ...technologyMarqueeItems].map((item, index) => (
                  <div
                    key={`${item.name}-${index}`}
                    className="technology-marquee-item"
                  >
                    <img src={item.src} alt={`${item.name} visual`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          */}
        </section>

        <section className="section-shell">
          <div className="workflow-layout">
            <article
              className="workflow-panel reveal-block"
              data-reveal
              style={revealStyle(80)}
            >
              <p className="section-kicker" style={{ textAlign: "left" }}>
                {sections.workflow.leftKicker}
              </p>
              <h2
                className="section-title"
                style={{
                  fontSize: "clamp(2rem, 4vw, 3.4rem)",
                  textAlign: "left",
                }}
              >
                {sections.workflow.leftTitle}
              </h2>
              <div className="workflow-rail">
                {workflowSteps.map((step) => (
                  <div key={step.step} className="workflow-step">
                    <span className="workflow-index">{step.step}</span>
                    <div>
                      <h3>{step.title}</h3>
                      <p>{step.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <article
              className="workflow-panel reveal-block"
              data-reveal
              style={revealStyle(170)}
            >
              <p
                className="section-kicker"
                style={{ textAlign: "left", fontSize: "1.4rem" }}
              >
                {sections.workflow.rightKicker}
              </p>
              <div className="highlight-band">
                {highlights.map((highlight) => (
                  <div className="highlight-tile" key={highlight.id}>
                    <strong>{highlight.title}</strong>
                    <span>{highlight.body}</span>
                  </div>
                ))}
              </div>
            </article>
          </div>

          <div
            style={{
              textAlign: "center",
              position: "relative",
              zIndex: 1,
              marginTop: "1.5rem",
            }}
          >
            <a
              href="/recruit"
              className="section-nav-btn reveal-block"
              data-reveal
              style={revealStyle(260)}
            >
              {actionButtons.joinTeam}
            </a>
          </div>
        </section>

        <section className="section-shell">
          <div className="section-head reveal-block" data-reveal>
            <p className="section-kicker">{sections.nextSection.kicker}</p>
            <h2 className="section-title">{sections.nextSection.title}</h2>
            <p className="section-copy">{sections.nextSection.copy}</p>
          </div>

          <div className="contact-grid">
            {contactCards.map((card, index) => (
              <article
                key={card.id}
                className="contact-card reveal-block"
                data-reveal
                style={revealStyle(70 + index * 80)}
              >
                <div className="contact-card-label">{card.label}</div>
                <div className="contact-card-value">{card.value}</div>
              </article>
            ))}
          </div>

          <div className="final-layout" style={{ marginTop: "1rem" }}>
            <article
              className="contact-panel reveal-block"
              data-reveal
              style={revealStyle(180)}
            >
              <p className="section-kicker" style={{ textAlign: "left" }}>
                {sections.nextMove.kicker}
              </p>
              <p className="contact-copy">{sections.nextMove.paragraph}</p>
              <div className="contact-actions">
                <a className="contact-action" href="/company">
                  {sections.nextMove.about}
                </a>
                <a className="contact-action" href="/technologies">
                  {sections.nextMove.technologies}
                </a>
                <a className="contact-action" href="/contact">
                  {sections.nextMove.contact}
                </a>
              </div>
            </article>

            <article
              className="contact-panel reveal-block"
              data-reveal
              style={revealStyle(260)}
            >
              <p className="section-kicker" style={{ textAlign: "left" }}>
                {sections.designIntent.kicker}
              </p>
              <p className="contact-copy">{sections.designIntent.paragraph}</p>
            </article>
          </div>

          <div
            className="footer-band reveal-block"
            data-reveal
            style={revealStyle(320)}
          >
            <div className="footer-top">
              <div className="footer-brand">
                <img src={eplusLogo} alt="Eplus logo" />
                <p className="footer-brand-desc">{cards.footer.brandDesc}</p>
                <div className="footer-social">
                  <a
                    href="mailto:support@eplus.co.kr"
                    className="footer-social-link"
                    title="Email"
                    style={{ fontSize: "1.4rem" }}
                  >
                    ✉
                  </a>
                  <a
                    href="tel:031-628-8600"
                    className="footer-social-link"
                    title="Phone"
                  >
                    ☎
                  </a>
                </div>
              </div>

              <div>
                <div className="footer-col-title">
                  {cards.footer.companyLinksTitle}
                </div>
                <div className="footer-links">
                  <a href="/company" className="footer-link">
                    {cards.footer.aboutEplus}
                  </a>
                  <a href="/technologies" className="footer-link">
                    {cards.footer.technologies}
                  </a>
                  <a href="/vendor" className="footer-link">
                    {cards.footer.vendorProducts}
                  </a>
                  <a href="/recruit" className="footer-link">
                    {cards.footer.careers}
                  </a>
                </div>
              </div>

              <div>
                <div className="footer-col-title">
                  {cards.footer.supportLinksTitle}
                </div>
                <div className="footer-links">
                  <a href="/contact" className="footer-link">
                    {cards.footer.contactUs}
                  </a>
                  <a href="mailto:support@eplus.co.kr" className="footer-link">
                    {cards.footer.supportEmail}
                  </a>
                  <a href="tel:031-628-8600" className="footer-link">
                    031-628-8600
                  </a>
                  <span className="footer-link">{cards.footer.fax}</span>
                </div>
              </div>

              <div>
                <div className="footer-col-title">
                  {cards.footer.officeTitle}
                </div>
                <div className="footer-links">
                  <span className="footer-info-item">
                    {cards.footer.address}
                  </span>
                </div>
              </div>
            </div>

            <hr className="footer-divider" />

            <div className="footer-bottom">
              <span>{cards.footer.copyright}</span>
              <span>{cards.footer.tagline}</span>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HalideLanding;
